import React from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Col,
    Collapse,
    Input,
    Popconfirm,
    Progress,
    Row,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    CaretRightOutlined,
    ClockCircleOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { anonApi } from '../util/anonymizerApi';

const TYPE_OPTIONS = [
    { label: 'Persons', value: 'person' },
    { label: 'Text (random)', value: 'text' },
    { label: 'Text (ML / remove PII)', value: 'mlText' },
    { label: 'Delete records', value: 'delete' }
];

const STATUS_COLORS = {
    running: 'processing',
    finished: 'success',
    finished_with_errors: 'warning',
    failed: 'error'
};

function formatDuration(run) {
    if (!run || !run.startedAt) return '';
    const end = run.finishedAt ? new Date(run.finishedAt) : new Date();
    const seconds = Math.round((end - new Date(run.startedAt)) / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function sumStats(run, key) {
    if (!run || !run.stats) return 0;
    return Object.keys(run.stats).reduce((acc, type) => acc + (run.stats[type][key] || 0), 0);
}

const { Text } = Typography;

const ChangeItem = ({ change }) => {
    if (change.action === 'delete') {
        return (
            <div style={{ marginBottom: 8 }}>
                <Tag color="red">delete</Tag>
                <b>{change.form}</b> — {change.count} record{change.count === 1 ? '' : 's'}
                {change.description && <> ({change.description})</>}
                <div><code style={{ fontSize: 12, wordBreak: 'break-all' }}>{change.query}</code></div>
            </div>
        );
    }
    return (
        <div style={{ marginBottom: 8 }}>
            <Tag color="blue">update</Tag>
            <b>{change.form}</b>
            {change.description && <> ({change.description})</>}
            {change.recordId && <> — <code>{change.recordId}</code></>}
            {change.query && (
                <div><code style={{ fontSize: 12, wordBreak: 'break-all' }}>{change.query}</code></div>
            )}
            <div style={{ paddingLeft: 12 }}>
                {change.fields &&
                    Object.keys(change.fields).map((f) => (
                        <div key={f} style={{ fontSize: 13 }}>
                            <b>{f}</b>:{' '}
                            <Text delete type="secondary">
                                {String(change.fields[f].old === null || change.fields[f].old === undefined ? '(empty)' : change.fields[f].old).substring(0, 120)}
                            </Text>{' '}
                            → <Text type="success">{String(change.fields[f].new).substring(0, 200)}</Text>
                        </div>
                    ))}
                {change.values &&
                    Object.keys(change.values).map((f) => (
                        <div key={f} style={{ fontSize: 13 }}>
                            <b>{f}</b> → <Text type="success">{String(change.values[f]).substring(0, 200)}</Text>
                        </div>
                    ))}
            </div>
        </div>
    );
};

const RunDetails = ({ run }) => {
    const steps = Object.keys(run.stats || {}).map((type) => ({ type, ...run.stats[type] }));
    const allErrors = steps.reduce(
        (acc, s) => acc.concat((s.errors || []).map((e) => ({ step: s.type, ...e }))),
        []
    );
    // hide columns that are zero for every step to keep the table readable
    const would = run.dryRun ? 'Would ' : '';
    const optionalColumns = [
        { title: `${would}Update${run.dryRun ? '' : 'd'}`, dataIndex: 'recordsUpdated', key: 'updated' },
        { title: `${would}${run.dryRun ? 'update related' : 'Related updates'}`, dataIndex: 'relatedUpdates', key: 'related' },
        { title: `${would}Delete${run.dryRun ? '' : 'd'}`, dataIndex: 'recordsDeleted', key: 'deleted' },
        { title: 'ML calls', dataIndex: 'mlCalls', key: 'ml' }
    ].filter((col) => steps.some((s) => (s[col.dataIndex] || 0) > 0));

    return (
        <>
            {allErrors.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${allErrors.length} error${allErrors.length > 1 ? 's' : ''} in this run`}
                    description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {allErrors.map((e, i) => (
                                <li key={i} style={{ marginBottom: 4 }}>
                                    <Tag>{e.step}</Tag>
                                    {e.form && <b>{e.form}: </b>}
                                    {e.message}
                                    {e.recordId && (
                                        <div>
                                            <code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                                                {e.recordId}
                                            </code>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    }
                />
            )}
            {run.dryRun && steps.some((s) => (s.changes || []).length > 0) && (
                <Collapse style={{ marginBottom: 12 }}>
                    {steps
                        .filter((s) => (s.changes || []).length > 0)
                        .map((s) => (
                            <Collapse.Panel
                                key={s.type}
                                header={`Planned changes — ${s.type} (${s.changes.length}${s.changesTruncated ? ` shown, ${s.changesTruncated} more` : ''})`}
                            >
                                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                                    {s.changes.map((c, i) => (
                                        <ChangeItem key={i} change={c} />
                                    ))}
                                </div>
                            </Collapse.Panel>
                        ))}
                </Collapse>
            )}
            <Table
                size="small"
                pagination={false}
                rowKey="type"
                dataSource={steps}
                columns={[
                    { title: 'Step', dataIndex: 'type', key: 'type' },
                    { title: 'Found', dataIndex: 'recordsFound', key: 'found' },
                    { title: 'Processed', dataIndex: 'recordsProcessed', key: 'processed' },
                    ...optionalColumns,
                    {
                        title: 'Duration',
                        dataIndex: 'durationMs',
                        key: 'duration',
                        render: (ms) => (ms ? `${Math.round(ms / 1000)}s` : '-')
                    },
                    {
                        title: 'Errors',
                        dataIndex: 'errors',
                        key: 'errors',
                        render: (errors) => (
                            <Tag color={errors && errors.length ? 'error' : 'success'}>
                                {(errors || []).length}
                            </Tag>
                        )
                    }
                ]}
            />
        </>
    );
};

const RunsPanel = () => {
    const [status, setStatus] = React.useState(null);
    const [runs, setRuns] = React.useState([]);
    const [schedule, setSchedule] = React.useState(null);
    const [selectedTypes, setSelectedTypes] = React.useState(TYPE_OPTIONS.map((o) => o.value));
    const [dryRun, setDryRun] = React.useState(true);
    const [starting, setStarting] = React.useState(false);
    const [savingSchedule, setSavingSchedule] = React.useState(false);

    const refresh = React.useCallback(() => {
        anonApi('/v1/status')
            .then((s) => {
                setStatus(s);
                if (!schedule) setSchedule(s.schedule);
            })
            .catch(() => {});
        anonApi('/v1/runs').then((r) => setRuns(Array.isArray(r) ? r : [])).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schedule]);

    React.useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 3000);
        return () => clearInterval(interval);
    }, [refresh]);

    const startRun = () => {
        setStarting(true);
        anonApi('/v1/runs', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ types: selectedTypes, dryRun })
        })
            .then(() => {
                message.success(dryRun ? 'Dry run started' : 'Anonymization run started');
                refresh();
            })
            .catch((e) => message.error(e.message))
            .finally(() => setStarting(false));
    };

    const saveSchedule = () => {
        setSavingSchedule(true);
        anonApi('/v1/schedule', {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(schedule)
        })
            .then((s) => {
                setSchedule(s);
                message.success('Schedule saved');
            })
            .catch((e) => message.error(e.message))
            .finally(() => setSavingSchedule(false));
    };

    const current = status && status.currentRun;
    const last = status && status.lastRun;

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={16}>
                <Col span={12}>
                    <Card
                        title={
                            <Space>
                                <Badge status={current ? 'processing' : 'default'} />
                                Current status
                            </Space>
                        }
                        extra={<Button icon={<ReloadOutlined />} size="small" onClick={refresh} />}
                    >
                        {current ? (
                            <>
                                <Progress
                                    percent={(current.progress && current.progress.percent) || 0}
                                    status="active"
                                />
                                <p>
                                    Running since {new Date(current.startedAt).toLocaleTimeString()} (
                                    {formatDuration(current)})
                                    {current.progress && current.progress.currentType && (
                                        <> — current step: <b>{current.progress.currentType}</b></>
                                    )}
                                    {current.dryRun && <Tag style={{ marginLeft: 8 }}>dry run</Tag>}
                                </p>
                            </>
                        ) : (
                            <>
                                <p>No run in progress.</p>
                                {last ? (
                                    <p>
                                        Last run: <Tag color={STATUS_COLORS[last.status]}>{last.status}</Tag>
                                        {new Date(last.startedAt).toLocaleString()} ({formatDuration(last)},{' '}
                                        {last.trigger}
                                        {last.dryRun ? ', dry run' : ''}) — {sumStats(last, 'recordsFound')}{' '}
                                        records found, {last.dryRun ? 'would update' : 'updated'}{' '}
                                        {sumStats(last, 'recordsUpdated')}
                                        {sumStats(last, 'relatedUpdates') > 0 && <> (+{sumStats(last, 'relatedUpdates')} related)</>},{' '}
                                        {last.dryRun ? 'would delete' : 'deleted'} {sumStats(last, 'recordsDeleted')}
                                    </p>
                                ) : (
                                    <p>No previous runs.</p>
                                )}
                            </>
                        )}
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Checkbox.Group
                                options={TYPE_OPTIONS}
                                value={selectedTypes}
                                onChange={setSelectedTypes}
                            />
                            <Space>
                                <Switch checked={dryRun} onChange={setDryRun} />
                                Dry run (no changes in the target systems)
                                <Popconfirm
                                    title="Start anonymization run?"
                                    description={
                                        dryRun
                                            ? 'Dry run: nothing will be changed.'
                                            : 'This will modify and delete data in the target systems!'
                                    }
                                    onConfirm={startRun}
                                    okText="Start"
                                >
                                    <Button
                                        type="primary"
                                        icon={<CaretRightOutlined />}
                                        loading={starting}
                                        disabled={!!current || selectedTypes.length === 0}
                                    >
                                        Start run
                                    </Button>
                                </Popconfirm>
                            </Space>
                        </Space>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title={<Space><ClockCircleOutlined />Schedule</Space>}>
                        {schedule && (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Space>
                                    <Switch
                                        checked={schedule.enabled}
                                        onChange={(enabled) => setSchedule({ ...schedule, enabled })}
                                    />
                                    Scheduling {schedule.enabled ? 'enabled' : 'disabled'}
                                </Space>
                                <Input
                                    addonBefore="Cron"
                                    value={schedule.cron}
                                    onChange={(e) => setSchedule({ ...schedule, cron: e.target.value })}
                                    placeholder="0 2 * * *"
                                />
                                <Checkbox.Group
                                    options={TYPE_OPTIONS}
                                    value={schedule.types && schedule.types.length ? schedule.types : TYPE_OPTIONS.map((o) => o.value)}
                                    onChange={(types) => setSchedule({ ...schedule, types })}
                                />
                                <Space>
                                    <Switch
                                        checked={schedule.dryRun}
                                        onChange={(v) => setSchedule({ ...schedule, dryRun: v })}
                                    />
                                    Scheduled runs as dry run
                                </Space>
                                <Button type="primary" loading={savingSchedule} onClick={saveSchedule}>
                                    Save schedule
                                </Button>
                                <Alert
                                    type="info"
                                    showIcon
                                    message='Cron format: "minute hour day month weekday", e.g. "0 2 * * *" = every day at 02:00.'
                                />
                            </Space>
                        )}
                    </Card>
                </Col>
            </Row>
            <Card title="Run history">
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={runs}
                    expandable={{
                        expandedRowRender: (run) => <RunDetails run={run} />,
                        expandRowByClick: true
                    }}
                    columns={[
                        {
                            title: 'Started',
                            dataIndex: 'startedAt',
                            key: 'startedAt',
                            render: (v) => new Date(v).toLocaleString()
                        },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (v, run) => (
                                <Space>
                                    <Tag color={STATUS_COLORS[v]}>{v.replace(/_/g, ' ')}</Tag>
                                    {run.dryRun && <Tag>dry run</Tag>}
                                    {run.trigger === 'schedule' && <Tag icon={<ClockCircleOutlined />}>scheduled</Tag>}
                                </Space>
                            )
                        },
                        {
                            title: 'Duration',
                            key: 'duration',
                            render: (_, run) => formatDuration(run)
                        },
                        {
                            title: 'Records',
                            key: 'records',
                            render: (_, run) => {
                                const updated = sumStats(run, 'recordsUpdated');
                                const related = sumStats(run, 'relatedUpdates');
                                const deleted = sumStats(run, 'recordsDeleted');
                                const would = run.dryRun ? 'would ' : '';
                                const parts = [`${sumStats(run, 'recordsFound')} found`];
                                if (updated || related) {
                                    parts.push(`${would}update${run.dryRun ? '' : 'd'} ${updated}` + (related ? ` (+${related} related)` : ''));
                                }
                                if (deleted) parts.push(`${would}delete${run.dryRun ? '' : 'd'} ${deleted}`);
                                return parts.join(' · ');
                            }
                        },
                        {
                            title: 'Errors',
                            key: 'errors',
                            render: (_, run) => {
                                const count = Object.keys(run.stats || {}).reduce(
                                    (acc, t) => acc + ((run.stats[t].errors || []).length),
                                    0
                                );
                                return <Tag color={count ? 'error' : 'success'}>{count}</Tag>;
                            }
                        }
                    ]}
                />
            </Card>
        </Space>
    );
};

export default RunsPanel;
